import React, { useEffect, useState } from "react";
import { styled, Box, useTheme } from "@mui/material";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { scroller } from "react-scroll";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import { AccountCircleOutlined, LogoutOutlined, SearchOutlined } from "@mui/icons-material";
import { Toaster } from "react-hot-toast";
import type { ContextValue, Size, AppContext, Store } from "../types";
import { Chevron, LiLink } from "../common/commonComps";
import { clearAllRedirect, currencyFormat, getSid, scrollerTo, scrollToTop } from "../common/utils";
import { TOPBARHEIGHT } from "../common/constants";
import { navigations } from "./account/components/navigation";
import StoreSlideOver from "./StoreSlideOver";

const Menu = styled("div")<{ header_color?: string; open: boolean }>(({ header_color, open }) => ({
    padding: "90px 40px 0",
    background: "#f9f9f9",
    borderLeft: `5px solid ${header_color}`,
    position: "fixed",
    right: open ? 0 : -300,
    zIndex: 3100,
    width: 300,
    height: "100%",
    transition: "all 0.5s ease 0s",
    top: 0,
}));

const Logo = styled("img")({
    display: "inline-block",
    maxHeight: "100%",
    maxWidth: "100%",
    verticalAlign: "top",
});

const DesktopMenu = styled("div")<{ open: boolean; context: AppContext }>(({ open, context }) => ({
    position: "absolute",
    left: `${(document.getElementById("userbox")?.getBoundingClientRect().x || 0) + 110}px`,
    background: "white",
    zIndex: 10000,
    width: 240,
    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 5px",
    overflow: "hidden",
    borderRadius: "0 0 4px 4px",
    height: open ? (navigations(context).length + 1) * 45 : 0,
    transition: "height 0.5s ease",
}));

const DMenuItem = styled("div")({
    height: 40,
    cursor: "pointer",
    fontWeight: 400,
    fontSize: 16,
    textAlign: "left",
    lineHeight: "40px",
    display: "inline-block",
    verticalAlign: "middle",
});

const NavItem = styled("div")<{ light?: boolean }>(({ light }) => ({
    display: "block",
    height: "100%",
    fontWeight: light ? 500 : 800,
    position: "relative",

    "& span": {
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
    },

    "&.mobile": {
        height: 45,
        fontSize: 20,
        lineHeight: "45px",
        width: "calc(100% - 35px)",
        verticalAlign: "middle",
        display: "inline-block",
    },
}));

const UserBox = styled("div")<{ size?: Size }>({
    display: "inline-block",
    verticalAlign: "middle",
    color: "#333",
    width: 320,
    lineHeight: "initial",
    textAlign: "right",

    "& div, & a": {},
});

const TopBar = styled("div")<{ header_color?: string }>({
    height: TOPBARHEIGHT,
    position: "absolute",
    width: "100%",
    top: 0,
    left: 0,
    background: "white",
    borderBottom: "1px solid #ededed",
});

const SearchBox = styled(Box)<{ size: Size }>(({ size, theme }) => ({
    display: `inline-block`,
    position: `relative`,
    transition: `opacity 500ms ease`,
    verticalAlign: `middle`,
    opacity: 0,
    width: `calc(100% - ${size !== `large` ? 140 : 540}px)`,

    "& input": {
        background: `#f5f5f5`,
        border: `2px solid ${theme.palette.account.accent}`,
        height: `40px`,
        width: `100%`,
        borderRadius: `${size !== `large` ? 30 : 5}px`,
        padding: `0 40px 0 20px`,
        fontSize: `16px`,
        color: `#333`,
        outline: `none`,
        "&::placeholder": {
            color: `#333`,
        },
    },
}));

const Hamburger = styled("div")<{ open: boolean }>(({ theme, open }) => ({
    width: 45,
    top: 30,
    position: "absolute",
    right: 5,
    height: 22,
    zIndex: 9999,
    cursor: "pointer",
    transition: "all 0.4s ease",

    "& > div": {
        position: "relative",
        top: 10,
        left: 0,
        background: theme.palette.account.accent || "#666",
        height: 2,
        width: "60%",
        transition: "all 0.4s ease",

        "&:before, &:after": {
            content: '""',
            position: "absolute",
            top: -10,
            background: theme.palette.account.accent || "#666",
            width: "100%",
            height: 2,
            transition: "all 0.4s ease",
        },

        "&:after": {
            top: 10,
        },
    },

    ...(open && {
        right: 20,

        "& > div": {
            background: "white",

            "&:before": {
                top: 0,
                transform: "rotate(45deg)",
                background: "black",
            },

            "&:after": {
                top: 0,
                transform: "rotate(135deg)",
                background: "black",
            },
        },
    }),
}));

const Wrap = styled("div")<{ size: Size }>(({ size }) => ({
    maxWidth: 1440,
    margin: "0 auto",
    height: "100%",
    padding: `0 ${size === "small" ? 10 : 20}px`,
    lineHeight: "80px",

    "&.nopad": {
        padding: 0,
        maxWidth: 1400,
    },
}));

const EditHeader = styled("div")({
    textAlign: "center",
    lineHeight: 1.5,
    background: "#2f6de9",
    color: "#333",
    fontWeight: 700,
    padding: 5,
    width: "100%",
    position: "fixed",
    bottom: 0,
    zIndex: 999999,
});

const Overlay = styled("div")<{ showOverlay: boolean; overlayDark: boolean }>(({ showOverlay, overlayDark }) => ({
    position: "absolute",
    top: 0,
    height: document.body.scrollHeight,
    left: 0,
    zIndex: 3000,
    right: 0,
    visibility: showOverlay ? "visible" : "hidden",
    opacity: overlayDark ? 1 : 0,
    background: "rgba(0, 0, 0, 0.35)",
    transition: "opacity 1s ease",
}));

const OuterHeader = styled("div")<{ tbh: number; size: Size; so?: boolean }>(({ tbh, size }) => ({
    minHeight: Math.trunc(tbh * (size === "small" ? 1.5 : 2)),
    width: "100%",
    top: 0,
    left: 0,
    zIndex: 999999,
    position: "fixed",
}));

const checkSearchBox = () => {
    const regSearch = document.getElementById(`layout-search-box`);
    const hs = document.getElementById(`hero-search`);
    if (!hs) {
        if (!regSearch) {
            window.requestAnimationFrame(() => {
                setTimeout(() => {
                    checkSearchBox();
                }, 100);
            });
            return;
        }
        regSearch?.style.setProperty(`opacity`, `1`);
        return;
    }

    const hsTop = hs.getBoundingClientRect().top;
    if (!regSearch) return;

    if (hsTop > 40 && regSearch.style.opacity !== `0`) {
        regSearch.style.setProperty(`opacity`, `0`);
        regSearch.querySelector<HTMLInputElement>(`input`)!.style.setProperty(`cursor`, `default`);
    } else if (hsTop < 40 && regSearch.style.opacity === `0`) {
        regSearch.style.setProperty(`opacity`, `1`);
        regSearch.querySelector<HTMLInputElement>(`input`)!.style.setProperty(`cursor`, `initial`);
    }
};

let csbTimer: ReturnType<typeof setTimeout>;
const csbTimed = () => {
    clearTimeout(csbTimer);
    csbTimer = setTimeout(checkSearchBox, 25);
};

const Alert = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof MuiAlert>>((props, ref) => (
    <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

const Layout = (value: ContextValue) => {
    const { save, size, loaded, context, user } = value;
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(``);
    const [active, setActive] = useState(``);
    const [searchOn, setSearchOn] = useState(false);
    const [dMenuOpen, setDMenuOpen] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [overlayDark, setOverlayDark] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const [storeSlide, setStoreSlide] = useState<Store | null>(null);
    const location = useLocation();
    const theme = useTheme();

    const goToCategories = () => {
        if (location.pathname === `/`) {
            scrollerTo(`categories`, -80);
        } else {
            navigate(`/`);
            setTimeout(() => scrollerTo(`categories`, -80), 100);
        }
    };

    const ScrollOffset = size === `small` ? 0 : -TOPBARHEIGHT - 10;

    const Col = styled(Box)<{ double?: boolean | string | number; height?: number }>(({ double, height }) => ({
        display: `inline-block`,
        verticalAlign: `middle`,
        "& div": {
            verticalAlign: `middle`,
        },
        height: height ?? (double ? 50 : 25),
        cursor: `pointer`,
        "&.chevron": {
            position: `relative`,
            lineHeight: `${TOPBARHEIGHT}px`,
            height: `${TOPBARHEIGHT}px`,
        },
    }));

    useEffect(() => {
        if (!location.search.includes(`q=`)) {
            setSearchTerm(``);
        }
        csbTimed();
    }, [location.pathname]);

    const closeAll = () => {
        setSearchOn(false);
        setDMenuOpen(false);
    };

    useEffect(() => {
        document.body.addEventListener(`click`, closeAll);
        return () => {
            document.body.removeEventListener(`click`, closeAll);
        };
    }, []);

    const searchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const isEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === `Enter`) {
            e.preventDefault();
            navigate(`/search?q=${searchTerm}`);
        }
    };

    const openDmenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setDMenuOpen(!dMenuOpen);
    };

    const handleClose = (event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === `clickaway`) {
            return;
        }
        setOpen(false);
    };

    const closeMenus = () => {
        if (showOverlay && !overlayDark) return;
        setOverlayDark(false);
        setTimeout(setShowOverlay, 1000);
        setStoreSlide(null);
        setMenuOpen(false);
        document.body.style.overflow = `initial`;
        document.querySelector(`.bm-burger-button`)?.classList.remove(`open`);
    };

    const menuStateChange = () => {
        if (menuOpen || storeSlide) {
            closeMenus();
        } else {
            document.body.style.overflow = `hidden`;
            document.querySelector(`.bm-burger-button`)?.classList.add(`open`);
            setShowOverlay(true);
            setOverlayDark(true);
            setMenuOpen(true);
        }
    };

    const saveConfig = () => {
        save().then(() => {
            setOpen(true);
        });
    };

    const setStore = (v: Store | null) => {
        if (v) {
            setStoreSlide(v);
            setOverlayDark(true);
            setShowOverlay(true);
            document.body.style.overflow = v ? `hidden` : `initial`;
        } else {
            closeMenus();
        }
    };

    const match = useLocation();

    const hash = match?.hash;
    const sid = getSid();
    if (!sid) {
        navigate(`/login`);
    }
    const edit = sessionStorage.getItem(`edit`);
    const programName = decodeURIComponent(sessionStorage.getItem(`programName`) ?? ``);

    useEffect(() => {
        if (location.pathname.startsWith(`/search`)) {
            setSearchTerm(searchParams.get(`q`) ?? ``);
        }
    }, [searchParams]);

    useEffect(() => {
        window.addEventListener(`scroll`, csbTimed);
        return () => window.removeEventListener(`scroll`, csbTimed);
    }, []);

    useEffect(() => {
        if (hash && hash.replace(`#`, ``) !== active) {
            setTimeout(() => {
                window.requestAnimationFrame(() => {
                    scroller.scrollTo(hash.replace(`#`, ``), {
                        duration: 800,
                        delay: 0,
                        smooth: `easeInOutQuart`,
                        offset: ScrollOffset,
                    });
                });
            }, 500);
        }
        if (location.pathname !== `/`) {
            setActive(``);
        }
        csbTimed();
    }, [location.pathname]);

    const car = () => clearAllRedirect(context);

    return (
        loaded && (
            <Box>
                <Toaster position="bottom-left" />
                <Snackbar
                    anchorOrigin={{ vertical: `top`, horizontal: `center` }}
                    open={open}
                    autoHideDuration={6000}
                    onClose={handleClose}
                >
                    <Alert onClose={handleClose} severity="success" sx={{ width: `100%` }}>
                        Saved
                    </Alert>
                </Snackbar>
                {!!edit && (
                    <EditHeader>
                        You are editing program {programName}. <br />
                        <button type="submit" onClick={saveConfig}>
                            SAVE CHANGES
                        </button>
                    </EditHeader>
                )}

                <OuterHeader {...{ size }} tbh={TOPBARHEIGHT} id="header" so={searchOn}>
                    <TopBar header_color={context.adminConfig?.mall?.main_color} id="topbar">
                        <Wrap size={size}>
                            <LiLink
                                style={{
                                    verticalAlign: `middle`,
                                    margin: `0 20px 0 0`,
                                    width: `${size !== `large` ? 45 : 200}px`,
                                    height: size !== `large` ? `auto` : `${TOPBARHEIGHT - 30}px`,
                                    display: `inline-block`,
                                }}
                                to="/"
                            >
                                <Logo
                                    alt="header logo"
                                    src={
                                        size !== `large`
                                            ? context.adminConfig?.mall?.Color_Mobile_Logo
                                            : context.adminConfig?.mall?.Color_Desktop_Logo
                                    }
                                />
                            </LiLink>
                            <SearchBox id="layout-search-box" size={size}>
                                <div onClick={() => navigate(`/search?q=${searchTerm}`)} />
                                <input
                                    onClick={e => e.stopPropagation()}
                                    id="searchbox"
                                    onFocus={() => setSearchOn(true)}
                                    onKeyDown={isEnter}
                                    placeholder="Search"
                                    onChange={searchOnChange}
                                    value={searchTerm}
                                />
                                <SearchOutlined
                                    sx={{ position: `absolute`, top: `8px`, right: `10px`, height: 25, width: 25 }}
                                />
                            </SearchBox>
                            {size === `large` && (
                                <UserBox id="userbox" size={size}>
                                    <Col sx={{ marginRight: `5px` }} onClick={goToCategories}>
                                        <GridViewOutlinedIcon sx={{ height: 25, width: 25 }} />
                                    </Col>
                                    <Col onClick={goToCategories} sx={{ marginRight: `40px` }}>
                                        <Box
                                            sx={{
                                                color: `#333`,
                                                fontSize: 16,
                                                fontWeight: 500,
                                                overflow: `hidden`,
                                                maxWidth: `100px`,
                                                display: `inline-block`,
                                                textOverflow: `ellipsis`,
                                                whiteSpace: `nowrap`,
                                                "&:hover": {
                                                    textDecoration: `underline`,
                                                },
                                            }}
                                        >
                                            Categories
                                        </Box>
                                    </Col>
                                    <Col height={28} onClick={openDmenu}>
                                        <AccountCircleOutlined sx={{ height: 28, width: 28 }} />
                                    </Col>
                                    <Col
                                        double={
                                            !!user?.redeemableAmt && context.adminConfig?.mall?.show_balance_in_top_bar
                                        }
                                        sx={{
                                            textAlign: `left`,
                                            marginLeft: `5px`,

                                            marginRight: `20px`,
                                        }}
                                        onClick={openDmenu}
                                    >
                                        <>
                                            <Box
                                                sx={{
                                                    color: `#333`,
                                                    fontSize: 16,
                                                    fontWeight: 500,
                                                    overflow: `hidden`,
                                                    maxWidth: `100px`,
                                                    display: `inline-block`,
                                                    textOverflow: `ellipsis`,
                                                    whiteSpace: `nowrap`,
                                                    "&:hover": {
                                                        textDecoration: `underline`,
                                                    },
                                                }}
                                            >
                                                {user?.firstName || user?.email?.split(`@`)[0]}
                                            </Box>
                                            <Chevron
                                                top={
                                                    user?.redeemableAmt &&
                                                    context.adminConfig?.mall?.show_balance_in_top_bar
                                                        ? 12
                                                        : 21
                                                }
                                            />
                                            {!!user?.redeemableAmt &&
                                                context.adminConfig?.mall?.show_balance_in_top_bar && (
                                                    <Box sx={{ fontSize: 14, color: `#333` }}>
                                                        ${currencyFormat(user?.redeemableAmt)}
                                                    </Box>
                                                )}
                                        </>
                                    </Col>
                                </UserBox>
                            )}
                            <StoreSlideOver store={storeSlide} {...{ setStore, size, context }} />
                            <Overlay {...{ showOverlay, overlayDark }} onClick={closeMenus} />
                            {size !== `large` && (
                                <>
                                    <Hamburger theme={theme} open={menuOpen} onClick={menuStateChange}>
                                        <div />
                                    </Hamburger>
                                    <Menu header_color={context.adminConfig?.mall?.main_color} open={menuOpen}>
                                        {navigations(context).map(({ path, name, icon: Icon }) => (
                                            <LiLink
                                                color="#333"
                                                onClick={() => {
                                                    setActive(``);
                                                    scrollToTop();
                                                    menuStateChange();
                                                }}
                                                key={path}
                                                to={path}
                                            >
                                                <Icon
                                                    style={{
                                                        verticalAlign: `middle`,
                                                        color: `#333`,
                                                        marginRight: `10px`,
                                                    }}
                                                />
                                                <NavItem light sx={{ fontWeight: 500 }} className="mobile">
                                                    {name}
                                                </NavItem>
                                            </LiLink>
                                        ))}
                                        <LiLink color="#333" onClick={car} key="logout" to="">
                                            <LogoutOutlined
                                                style={{ verticalAlign: `middle`, color: `#333`, marginRight: `10px` }}
                                            />
                                            <NavItem light className="mobile">
                                                Logout
                                            </NavItem>
                                        </LiLink>
                                    </Menu>
                                </>
                            )}
                        </Wrap>

                        <DesktopMenu context={context} id="desktopmenu" open={dMenuOpen}>
                            {navigations(context).map(({ path, name, icon: Icon }) => (
                                <LiLink
                                    sx={{ marginTop: `5px` }}
                                    color="#333"
                                    className="dblock bghover"
                                    onClick={() => {
                                        setActive(``);
                                        closeAll();
                                        scroller.scrollTo(`top`, {});
                                    }}
                                    key={path}
                                    to={path}
                                >
                                    <Icon style={{ verticalAlign: `middle`, marginRight: `15px`, color: `#666` }} />
                                    <DMenuItem>{name}</DMenuItem>
                                </LiLink>
                            ))}
                            <LiLink color="#333" onClick={car} className="dblock bghover" key="logout" to="">
                                <LogoutOutlined
                                    style={{ verticalAlign: `middle`, marginRight: `15px`, color: `#333` }}
                                />
                                <DMenuItem>Logout</DMenuItem>
                            </LiLink>
                        </DesktopMenu>
                    </TopBar>
                </OuterHeader>
                <Outlet context={{ setStore }} />
            </Box>
        )
    );
};

export default Layout;
